import { getMailApiBase } from "@/auth/paths";
import { mailAuthHeaders } from "@/auth/session";
import { prepareAccountPhotoForUpload } from "@/lib/mail/accountPhotoUpload";
import type { ApiResult } from "@/lib/api/client";

export function resolveMailAccountPhotoUrl(relativeOrAbsolute: string | null | undefined): string | null {
  if (!relativeOrAbsolute) return null;
  if (relativeOrAbsolute.startsWith("blob:") || relativeOrAbsolute.startsWith("data:")) {
    return relativeOrAbsolute;
  }
  if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
  const rel = relativeOrAbsolute.replace(/^\//, "");
  return `${getMailApiBase()}/${rel}`;
}

export async function uploadMailAccountPhoto(
  accountId: number,
  file: File
): Promise<ApiResult<{ success: boolean; photoUrl: string; photoVersion: number }>> {
  const prepared = await prepareAccountPhotoForUpload(file);
  const form = new FormData();
  form.append("accountId", String(accountId));
  form.append("photo", prepared);

  const url = `${getMailApiBase()}/accounts/photo.php`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { ...mailAuthHeaders() },
    body: form,
  });

  let body: { success?: boolean; message?: string; photoUrl?: string; photoVersion?: number } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    body = {};
  }

  if (!res.ok || body.success === false) {
    return {
      ok: false,
      status: res.status,
      message: body.message || `Erreur HTTP ${res.status}`,
    };
  }

  return {
    ok: true,
    data: {
      success: true,
      photoUrl: body.photoUrl ?? "",
      photoVersion: body.photoVersion ?? 0,
    },
  };
}

export async function deleteMailAccountPhoto(
  accountId: number
): Promise<ApiResult<{ success: boolean; photoVersion: number; photoUrl: null }>> {
  const url = `${getMailApiBase()}/accounts/photo.php`;
  const res = await fetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...mailAuthHeaders(),
    },
    body: JSON.stringify({ accountId }),
  });

  let body: { success?: boolean; message?: string; photoVersion?: number; photoUrl?: null } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    body = {};
  }

  if (!res.ok || body.success === false) {
    return {
      ok: false,
      status: res.status,
      message: body.message || `Erreur HTTP ${res.status}`,
    };
  }

  return {
    ok: true,
    data: {
      success: true,
      photoVersion: body.photoVersion ?? 0,
      photoUrl: null,
    },
  };
}

export async function fetchMailAccountPhotoBlob(relativeUrl: string): Promise<string | null> {
  const url = resolveMailAccountPhotoUrl(relativeUrl);
  if (!url) return null;

  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: { ...mailAuthHeaders() },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
